import * as Sentry from "@sentry/react";

export const SentryTest = () => {
  const throwError = () => {
    throw new Error("Frontend Sentry test error!");
  };

  const captureError = () => {
    try {
      throw new Error("Captured frontend error");
    } catch (error) {
      Sentry.captureException(error);
      alert("Error captured and sent to Sentry! Check your console and Sentry dashboard.");
    }
  };

  const captureMessage = () => {
    Sentry.captureMessage("Test message from frontend", "info");
    alert("Message sent to Sentry! Check your Sentry dashboard.");
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-8">
      <div className="max-w-2xl w-full bg-card rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-foreground mb-4">
          🔴 Sentry Test Page (Frontend)
        </h1>
        <p className="text-muted-foreground mb-6">
          Use these buttons to test your Sentry integration for the React frontend.
        </p>

        <div className="space-y-4">
          {/* Test 1: Throw Error */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-2">Test 1: Throw Unhandled Error</h3>
            <p className="text-sm text-muted-foreground mb-3">
              This will throw an error that will be caught by React's error boundary
              and automatically sent to Sentry.
            </p>
            <button
              onClick={throwError}
              className="bg-red-500 hover:bg-red-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Throw Error
            </button>
          </div>

          {/* Test 2: Capture Exception */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-2">Test 2: Capture Exception</h3>
            <p className="text-sm text-muted-foreground mb-3">
              This will catch an error and manually send it to Sentry using
              `Sentry.captureException()`.
            </p>
            <button
              onClick={captureError}
              className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Capture Exception
            </button>
          </div>

          {/* Test 3: Capture Message */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-2">Test 3: Capture Message</h3>
            <p className="text-sm text-muted-foreground mb-3">
              This will send a simple message to Sentry using
              `Sentry.captureMessage()`.
            </p>
            <button
              onClick={captureMessage}
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Send Message
            </button>
          </div>

          {/* Verification */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <h3 className="font-semibold text-lg mb-2">✅ Verification Steps</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Open your browser's Developer Console (F12)</li>
              <li>Click any of the buttons above</li>
              <li>Look for console logs showing the event being sent</li>
              <li>Go to <a href="https://sentry.io" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">sentry.io</a></li>
              <li>Navigate to your project → Issues</li>
              <li>You should see the error/message appear within seconds!</li>
            </ol>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-blue-600 hover:text-blue-700 underline"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

// Export with Sentry's ErrorBoundary wrapper for better error capture
export default Sentry.withErrorBoundary(SentryTest, {
  fallback: ({ error, resetError }) => (
    <div className="min-h-screen bg-muted flex items-center justify-center p-8">
      <div className="max-w-lg w-full bg-card rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-red-600 mb-4">
          🔴 Error Caught by Sentry!
        </h1>
        <p className="text-muted-foreground mb-4">
          An error occurred and was automatically sent to Sentry:
        </p>
        <pre className="bg-muted p-4 rounded text-sm overflow-auto mb-4">
          {(error as Error).toString()}
        </pre>
        <p className="text-sm text-muted-foreground mb-4">
          ✅ Check your Sentry dashboard to see this error!
        </p>
        <button
          onClick={resetError}
          className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  ),
  showDialog: false,
});


